import React, { useEffect, useRef, useState } from "react";
import cn from "clsx";
import { isTouchScreen } from "../../../util";
import useSwiperScript from "@/hooks/useSwiperScript";
import { useIsRTL } from "@/hooks";
import GlobalShippingWhiteIcon from "@/images/global_shipping_background_white.svg";
import "./style.scss";

const ProductSizes = ({ sizes, gridSize }) => {
  if (!sizes?.length) return null;

  const swiperRef = useRef(null);
  const swiperInstanceRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const loadedScriptStatus = useSwiperScript();
  const isArabic = useIsRTL();
  const useSwiperAtAll = !isTouchScreen() && sizes.length > 5;
  const shouldInitSwiper = useSwiperAtAll && isInView;

  useEffect(() => {
    if (!useSwiperAtAll || !containerRef.current) return;

    const el = containerRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsInView(true);
      },
      { rootMargin: "100px", threshold: 0.01 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [useSwiperAtAll]);

  useEffect(() => {
    if (!shouldInitSwiper) return;

    if (
      swiperRef.current &&
      sizes.length &&
      (loadedScriptStatus || window.Swiper)
    ) {
      const swiperParams = {
        dots: false,
        slidesPerView: "auto" as const,
        grabCursor: true,
        slidesPerGroup: sizes.length,
        ...(!isTouchScreen() && {
          spaceBetween: 8,
          navigation: {
            prevEl: ".swiper-button-prev",
            nextEl: ".swiper-button-next"
          }
        })
      };
      //@ts-ignore
      swiperInstanceRef.current = new window.Swiper(
        swiperRef.current,
        swiperParams
      );
      isArabic && swiperInstanceRef.current?.changeLanguageDirection?.("rtl");
    }
    return () => {
      swiperInstanceRef.current?.destroy?.(true, true);
      swiperInstanceRef.current = null;
    };
  }, [sizes, loadedScriptStatus, shouldInitSwiper, isArabic]);

  const usePlainScroll = !shouldInitSwiper;

  return (
    <div
      ref={containerRef}
      className={cn("product-size-wrapper", {
        "product-sizes-plain": usePlainScroll
      })}
    >
      <ul
        className={cn("products_size_list", {
          smaller_list: gridSize > 4
        })}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className="swiper" ref={swiperRef}>
          <div className="swiper-wrapper">
            {sizes.map((size, index) => {
              const label = size.sizeShortLabel || size.size;
              const isGlobalShipping = label && size?.isGlobalShipping;
              return (
                <li
                  className={cn("swiper-slide", {
                    global_shipping: isGlobalShipping
                  })}
                  key={size.sizeId || index}
                >
                  {label}
                  {isGlobalShipping && (
                    <img
                      src={GlobalShippingWhiteIcon}
                      alt="Global Shipping Icon on size"
                      className="size_global_shipping_icon"
                    />
                  )}
                </li>
              );
            })}
          </div>
        </div>
      </ul>
    </div>
  );
};

export default ProductSizes;
